import React, { useEffect, useState } from 'react';
import { ArrowRight, Play, Shield, Zap, Target } from 'lucide-react';
import { mockData } from '../mock';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleCTAClick = () => {
    const contactSection = document.querySelector('#contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background with Matrix Effect */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${mockData.hero.backgroundImage})`,
            filter: 'brightness(0.3) contrast(1.2)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/90" />
        
        {/* Animated Grid Overlay */}
        <div className="absolute inset-0 cyber-grid opacity-20" />
        
        {/* Floating Particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-cyan-400 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className={`space-y-8 ${isVisible ? 'animate-fadeInLeft' : 'opacity-0'}`}>
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-400/30 rounded-full glass-effect">
              <Shield className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-medium text-cyan-400">Especialistas em Cibersegurança</span>
            </div>

            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="display-huge bg-gradient-to-r from-white via-cyan-200 to-blue-200 bg-clip-text text-transparent leading-tight">
                {mockData.hero.title}
              </h1>
              <h2 className="display-large text-cyan-400 font-bold animate-pulse">
                {mockData.hero.subtitle}
              </h2>
            </div>

            {/* Description */}
            <p className="body-large text-gray-300 max-w-2xl leading-relaxed">
              {mockData.hero.description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button 
                onClick={handleCTAClick}
                className="btn-primary group"
              >
                <span>{mockData.hero.ctaText}</span>
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              
              <button className="btn-secondary group">
                <Play className="w-5 h-5" />
                <span>Ver Demonstração</span>
              </button>
            </div>

            {/* Quick Stats */}
            <div className="flex items-center gap-8 pt-8">
              {mockData.stats.slice(0, 3).map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl font-bold text-cyan-400">{stat.value}</div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Animated Icons */}
          <div className={`relative ${isVisible ? 'animate-fadeInRight' : 'opacity-0'}`}>
            <div className="relative w-full h-96 lg:h-[500px]">
              {/* Central Shield */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="relative">
                  <div className="w-32 h-32 border-4 border-cyan-400 rounded-full flex items-center justify-center glass-effect animate-pulse">
                    <Shield className="w-16 h-16 text-cyan-400" />
                  </div>
                  <div className="absolute inset-0 w-32 h-32 border border-cyan-400/30 rounded-full animate-ping" />
                </div>
              </div>

              {/* Orbiting Elements */}
              <div className="absolute top-1/4 left-1/4 animate-pulse">
                <div className="w-16 h-16 border-2 border-blue-500 rounded-full flex items-center justify-center glass-effect">
                  <Zap className="w-8 h-8 text-blue-500" />
                </div>
              </div>

              <div className="absolute top-1/4 right-1/4 animate-pulse" style={{ animationDelay: '1s' }}>
                <div className="w-16 h-16 border-2 border-cyan-400 rounded-full flex items-center justify-center glass-effect">
                  <Target className="w-8 h-8 text-cyan-400" />
                </div>
              </div>

              <div className="absolute bottom-1/4 left-1/3 animate-pulse" style={{ animationDelay: '2s' }}>
                <div className="w-20 h-20 border-2 border-blue-400 rounded-full flex items-center justify-center glass-effect">
                  <Shield className="w-10 h-10 text-blue-400" />
                </div>
              </div>

              {/* Connecting Lines */}
              <div className="absolute inset-0">
                <svg className="w-full h-full opacity-30">
                  <line x1="50%" y1="50%" x2="25%" y2="25%" stroke="url(#gradient)" strokeWidth="2" strokeDasharray="5,5">
                    <animate attributeName="stroke-dashoffset" values="0;10" dur="2s" repeatCount="indefinite" />
                  </line>
                  <line x1="50%" y1="50%" x2="75%" y2="25%" stroke="url(#gradient)" strokeWidth="2" strokeDasharray="5,5">
                    <animate attributeName="stroke-dashoffset" values="0;10" dur="2s" repeatCount="indefinite" />
                  </line>
                  <line x1="50%" y1="50%" x2="33%" y2="75%" stroke="url(#gradient)" strokeWidth="2" strokeDasharray="5,5">
                    <animate attributeName="stroke-dashoffset" values="0;10" dur="2s" repeatCount="indefinite" />
                  </line>
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#22D3EE" />
                      <stop offset="100%" stopColor="#3B82F6" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="flex flex-col items-center space-y-2 animate-bounce">
          <div className="w-6 h-10 border-2 border-cyan-400 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-cyan-400 rounded-full mt-2 animate-pulse" />
          </div>
          <span className="text-xs text-gray-400">Role para baixo</span>
        </div>
      </div>
    </section>
  );
};

export default Hero;